import { api, buildQuery, download } from "./client";
import type {
  ActivityDetail,
  ActivityEvidence,
  ActivityListItem,
  ActivityParticipant,
  PageResult,
} from "./types";
import type { ParticipantType } from "@academichub/shared";

/** Endpoint kegiatan, peserta, dan bukti. */

export interface ActivityListParams {
  page?: number;
  perPage?: number;
  q?: string;
  status?: string;
  validationStatus?: string;
  categoryId?: string;
  academicYearId?: string;
  semesterId?: string;
  studyProgramId?: string;
  from?: string;
  to?: string;
  sort?: string;
  order?: string;
}

export function listActivities(params: ActivityListParams = {}) {
  return api.get<PageResult<ActivityListItem>>(`/activities${buildQuery({ ...params })}`);
}

export function getActivity(id: string) {
  return api.get<ActivityDetail>(`/activities/${id}`);
}

export interface ActivityInput {
  title: string;
  description: string;
  activityDate: string;
  startTime?: string;
  endTime?: string;
  location: string;
  categoryId: string;
  academicYearId: string;
  semesterId: string;
  organizer: string;
  responsiblePerson: string;
  output?: string;
  outcome?: string;
  externalLink?: string;
  studyProgramId?: string;
  unitId?: string;
}

export function createActivity(input: ActivityInput) {
  return api.post<ActivityDetail>("/activities", input);
}

export function updateActivity(id: string, input: Partial<ActivityInput>) {
  return api.patch<ActivityDetail>(`/activities/${id}`, input);
}

export function deleteActivity(id: string) {
  return api.del<{ message: string }>(`/activities/${id}`);
}

export function submitActivity(id: string) {
  return api.post<ActivityDetail>(`/activities/${id}/submit`);
}

export function reviewActivity(id: string, notes?: string) {
  return api.post<ActivityDetail>(`/activities/${id}/review`, { notes });
}

export function validateActivity(id: string, decision: "APPROVED" | "REJECTED", notes?: string) {
  return api.post<ActivityDetail>(`/activities/${id}/validate`, { decision, notes });
}

export function archiveActivity(id: string) {
  return api.post<ActivityDetail>(`/activities/${id}/archive`);
}

export function reviseActivity(id: string) {
  return api.post<ActivityDetail>(`/activities/${id}/revise`);
}

export function listParticipants(id: string) {
  return api.get<{ items: ActivityParticipant[] }>(`/activities/${id}/participants`);
}

export interface ParticipantInput {
  name: string;
  roleInActivity: string;
  participantType: ParticipantType;
  userId?: string;
}

export function addParticipant(id: string, input: ParticipantInput) {
  return api.post<ActivityParticipant>(`/activities/${id}/participants`, input);
}

export function removeParticipant(id: string, participantId: string) {
  return api.del<{ message: string }>(`/activities/${id}/participants/${participantId}`);
}

export interface EvidenceInput {
  file: File;
  evidenceType: string;
  description?: string;
}

export function uploadEvidence(id: string, input: EvidenceInput) {
  const form = new FormData();
  form.append("evidenceType", input.evidenceType);
  if (input.description) form.append("description", input.description);
  form.append("file", input.file);
  return api.upload<ActivityEvidence>(`/activities/${id}/evidence`, form);
}

export function deleteEvidence(evidenceId: string) {
  return api.del<{ message: string }>(`/evidence/${evidenceId}`);
}

export function fetchEvidenceBlob(evidenceId: string) {
  return download(`/evidence/${evidenceId}/download`);
}

/** Simpan blob ke berkas unduhan dengan nama yang diberikan. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
